import React, { useState } from 'react';
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { api } from '../../lib/api.js';
import { Field, Text, Area, ObjectList, FilePicker, UploadButton } from './fields.jsx';
import { Empty } from '../../components/states.jsx';
import { formatDate, formatPoints } from '../../lib/date.js';

const blank = () => ({
  id: null, number: '', title: '', summary: '', sections: [], deadline: '', points: '5', template: '',
});

const toForm = (assignment) => ({
  id: assignment.id,
  number: String(assignment.number ?? ''),
  title: assignment.title,
  summary: assignment.summary || '',
  sections: assignment.sections || [],
  deadline: assignment.deadline || '',
  points: String(assignment.points ?? 0),
  template: assignment.template || '',
});

export default function AssignmentsAdmin({ year, files, notify, reload, reloadFiles }) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!year) return null;
  const assignments = year.assignments || [];
  const set = (patch) => setDraft((current) => ({ ...current, ...patch }));

  const save = async (event) => {
    event.preventDefault();
    if (!draft.title.trim()) return notify('Vyplňte název úkolu.', 'error');

    setBusy(true);
    const payload = {
      yearId: year.id,
      number: draft.number === '' ? undefined : Number(draft.number),
      title: draft.title,
      summary: draft.summary,
      sections: draft.sections.filter((s) => s.body?.trim() || s.heading?.trim()),
      deadline: draft.deadline || null,
      points: Number(draft.points) || 0,
      template: draft.template || null,
    };

    try {
      if (draft.id) await api.admin.updateAssignment(draft.id, payload);
      else await api.admin.createAssignment(payload);
      await reload();
      setDraft(null);
      notify(draft.id ? 'Úkol uložen.' : 'Úkol přidán.');
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (assignment) => {
    if (!window.confirm(`Opravdu smazat úkol „${assignment.title}"?`)) return;
    try {
      await api.admin.deleteAssignment(assignment.id);
      await reload();
      notify('Úkol smazán.');
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  const move = async (index, delta) => {
    const next = [...assignments];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    try {
      await api.admin.reorder('assignments', next.map((a) => a.id));
      await reload();
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  return (
    <>
      <div className="admin-title">
        <div>
          <h1>Úkoly</h1>
          <p>Ročník {year.label} · {assignments.length} úkolů</p>
        </div>
        {!draft && (
          <button type="button" className="btn btn-primary" onClick={() => setDraft(blank())}>
            <Plus size={16} /> Přidat úkol
          </button>
        )}
      </div>

      {draft && (
        <form className="editor" onSubmit={save} style={{ marginBottom: '1.5rem' }}>
          <h2>{draft.id ? 'Úprava úkolu' : 'Nový úkol'}</h2>

          <Field label="Název" id="nazev-ukolu" hint={`Bez „Úkol 1:" — číslo se doplní samo`}>
            <Text id="nazev-ukolu" value={draft.title} onChange={(v) => set({ title: v })} placeholder="Např. Součin prvků až na aktuální" autoFocus />
          </Field>

          <div className="field-row">
            <Field label="Číslo úkolu" id="cislo-ukolu" hint="Prázdné = další v pořadí">
              <Text id="cislo-ukolu" type="number" min="1" value={draft.number} onChange={(v) => set({ number: v })} />
            </Field>
            <Field label="Termín odevzdání" id="deadline">
              <Text id="deadline" type="date" value={draft.deadline} onChange={(v) => set({ deadline: v })} />
            </Field>
            <Field label="Body" id="body-ukolu">
              <Text id="body-ukolu" type="number" min="0" value={draft.points} onChange={(v) => set({ points: v })} />
            </Field>
          </div>

          <Field
            label="Úvodní odstavec"
            id="perex"
            hint="Text obalený zpětnými apostrofy se zobrazí jako `kód`. Prázdný řádek dělá nový odstavec."
          >
            <Area id="perex" value={draft.summary} onChange={(v) => set({ summary: v })} rows={4} />
          </Field>

          <Field label="Části zadání" hint={`Nadpis + text, např. „Příklad", „Unit testy", „Odevzdání".`}>
            <ObjectList
              value={draft.sections}
              onChange={(sections) => set({ sections })}
              empty={{ heading: '', body: '' }}
              addLabel="Přidat část"
              fields={[
                { key: 'heading', label: 'Nadpis části' },
                { key: 'body', label: 'Text', type: 'textarea' },
              ]}
            />
          </Field>

          <Field label="Šablona ke stažení" id="sablona" hint="Sponka vybírá z nahraných souborů, šipka nahraje nový.">
            <div style={{ display: 'flex', gap: '.4rem' }}>
              <Text id="sablona" value={draft.template} onChange={(v) => set({ template: v })} placeholder="/uploads/sablona.py" />
              <FilePicker files={files} onNeedFiles={reloadFiles} onPick={(url) => set({ template: url })} />
              <UploadButton notify={notify} onNeedFiles={reloadFiles} onUploaded={(file) => set({ template: file.url })} />
            </div>
          </Field>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Ukládám…' : 'Uložit'}</button>
            <button type="button" className="btn btn-ghost" onClick={() => setDraft(null)}>Zrušit</button>
          </div>
        </form>
      )}

      {assignments.length === 0 && !draft ? (
        <Empty title="Zatím žádné úkoly">Přidejte první úkol tlačítkem nahoře.</Empty>
      ) : (
        <div className="stack-tight">
          {assignments.map((assignment, index) => (
            <div className="admin-row" key={assignment.id}>
              <div className="admin-row-order">
                <button type="button" className="icon-btn" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Posunout nahoru">
                  <ChevronUp size={13} />
                </button>
                <button type="button" className="icon-btn" onClick={() => move(index, 1)} disabled={index === assignments.length - 1} aria-label="Posunout dolů">
                  <ChevronDown size={13} />
                </button>
              </div>

              <div className="admin-row-main">
                <div className="admin-row-title">{assignment.number}. {assignment.title}</div>
                <div className="admin-row-sub">
                  {assignment.deadline && <span>{formatDate(assignment.deadline)}</span>}
                  <span>{formatPoints(assignment.points)}</span>
                  <span>{assignment.sections?.length || 0} částí</span>
                  {assignment.template && <span>šablona</span>}
                </div>
              </div>

              <div className="admin-row-actions">
                <button type="button" className="icon-btn" onClick={() => setDraft(toForm(assignment))} aria-label={`Upravit ${assignment.title}`}>
                  <Pencil size={15} />
                </button>
                <button type="button" className="icon-btn icon-btn-danger" onClick={() => remove(assignment)} aria-label={`Smazat ${assignment.title}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
